/**
 * Build domain-based navigation sections for the Nirman App Shell.
 *
 * Maps existing navigation items into Nirman domain hierarchy using
 * EXCLUSIVE, HIERARCHICAL matching to ensure each item appears in exactly ONE domain.
 */

import React from 'react';
import { NIRMAN_DOMAINS, DOMAIN_ICON_MAP } from './domainConfig';
import { getRoleVisibleDomains } from './getRoleVisibleDomains';

/**
 * Assign an item to exactly ONE domain using hierarchical priority matching.
 * Returns the domain ID if assigned, null otherwise.
 *
 * Priority hierarchy (most specific → most generic):
 * 1. Route-based (most specific)
 * 2. Primary domain keywords (exclusive — stop after first match)
 * 3. Secondary domain keywords
 *
 * @param {Object} item - Navigation item
 * @returns {string|null} - Domain ID or null if not assigned
 */
function assignItemToDomain(item) {
  const label = item.label?.toLowerCase() || '';
  const path = item.path?.toLowerCase() || '';

  // 1. ROUTE-BASED ASSIGNMENT (most specific — checked first)
  if (path.includes('/assessment') || path.includes('/assignments')) return 'assessments';
  if (path.includes('/placement')) return 'placement';
  if (path.includes('/notification') || path.includes('/inbox')) return 'notifications';

  // 2. PRIMARY DOMAIN KEYWORDS (mutually exclusive)
  if (label.includes('assessment')) return 'assessments';
  if (label.includes('placement')) return 'placement';
  if (label.includes('notification') || label.includes('message')) return 'notifications';

  // 3. SETTINGS/PROFILE KEYWORDS (before generic analytics)
  if (label.includes('setting') || label.includes('profile') ||
      label.includes('recovery') || label.includes('backup') ||
      label.includes('access') || label.includes('monitoring') ||
      label.includes('integrity') || label.includes('data integrity') ||
      label.includes('health') || label.includes('purge') ||
      label.includes('sync') || label.includes('claim') ||
      label.includes('handle')) return 'settings';

  // 4. LEARN DOMAIN (course/lesson keywords)
  if (label.includes('lesson') || label.includes('curriculum') ||
      label.includes('course') || label.includes('diary')) return 'learn';

  // 5. PRACTICE DOMAIN (coding-related keywords)
  // Note: Exclude 'problem analytics' which is progress domain
  if (label.includes('assignment') || label.includes('workspace') ||
      label.includes('batch') || label.includes('coding') ||
      label.includes('lab') || label.includes('practice')) return 'practice';

  // Plagiarism is coding-related (workspace origins)
  if (label.includes('plagiarism')) return 'practice';

  // Problem Library is practice domain (unless it's 'Problem Analytics' which is progress)
  if (label.includes('problem') && !label.includes('analytics')) return 'practice';

  // Intelligence features related to coding
  if (label.includes('intelligence') && label.includes('problem')) return 'practice';

  // 6. PROGRESS DOMAIN (analytics-related, but NOT practice analytics)
  // Catches: 'Overall Analytics', 'Analytics', 'Reports', 'Teaching Reports', 'Insights', 'Effectiveness'
  if (label.includes('analytics') || label.includes('report') ||
      label.includes('insight') || label.includes('effectiveness') ||
      label.includes('progress')) return 'progress';

  // 7. COMMUNITY DOMAIN (users, students, activity)
  // Note: 'Users' label is generic — assign to community in user management context
  if (label.includes('student') || label.includes('activity') ||
      label.includes('feed') || label.includes('community')) return 'community';

  // Generic 'Users' goes to community (user collaboration context)
  if (label.includes('user')) return 'community';

  // 8. HOME DOMAIN (dashboard/overview — only one per role)
  if (label.includes('dashboard') || label.includes('overview')) return 'home';

  return null;
}

/**
 * Convert a domain ID to a section structure compatible with BaseDashboardLayout.
 *
 * @param {string} domainId - Nirman domain ID (home, learn, practice, etc.)
 * @param {Array} items - Navigation items that belong to this domain
 * @returns {Object} Section object for BaseDashboardLayout
 */
function domainToSection(domainId, items) {
  const domain = NIRMAN_DOMAINS[domainId];
  if (!domain) return null;

  const IconComponent = DOMAIN_ICON_MAP[domain.icon];
  const icon = IconComponent ? <IconComponent fontSize="small" /> : null;

  return {
    id: domain.id,
    title: domain.label,
    collapsible: domain.collapsible !== false,
    items: items.map(item => ({
      id: item.id,
      label: item.label,
      path: item.path,
      ...(item.matchPath && { matchPath: item.matchPath }),
      icon: item.icon || icon,
      ...(item.action && { action: item.action }),
    })),
  };
}

/**
 * Build domain-based navigation sections for a role using EXCLUSIVE matching.
 *
 * Each item is assigned to exactly ONE canonical domain. Items are processed
 * through hierarchical priority matching to avoid duplicates.
 *
 * @param {Array} allNavItems - All available navigation items (from getVisibleNavSections)
 * @param {string} role - User role (student, faculty, admin, hod, placement)
 * @returns {Array} Sections organized by Nirman domain (no duplicates)
 */
export function buildDomainSections(allNavItems, role) {
  if (!role) return [];

  const flatItems = allNavItems.flatMap(section => section.items);
  const visibleDomainIds = getRoleVisibleDomains(role);

  // Initialize empty domain map
  const domainItemMap = {};
  visibleDomainIds.forEach(domainId => {
    domainItemMap[domainId] = [];
  });

  // Assign each item to exactly ONE domain (exclusive matching)
  flatItems.forEach(item => {
    const domainId = assignItemToDomain(item);
    if (domainId && domainItemMap[domainId]) {
      domainItemMap[domainId].push(item);
    }
  });

  // Build sections for visible domains with items
  const sections = visibleDomainIds
    .map(domainId => {
      const items = domainItemMap[domainId] || [];
      return domainToSection(domainId, items);
    })
    .filter(section => section && section.items.length > 0);

  return sections;
}

export default buildDomainSections;
